import React, { useState, useEffect, useRef } from "react";
import "./ImageLoader.css";
import defaultLogo from "../Logo/DIET_ACM_Logo.png";

/**
 * ImageLoader Component for Admin Panel
 * Displays the glowing DIET ACM logo while the image is loading over the network,
 * then smoothly fades in the target image.
 */
const ImageLoader = ({
  src,
  alt = "ACM Media",
  className = "",
  wrapperClassName = "",
  style = {},
  wrapperStyle = {},
  logoSrc = defaultLogo,
  loading = "lazy",
  onClick,
  title,
  onLoad,
  onError,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Reset loading state when source URL changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Check if image is already cached by browser
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    setHasError(false);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    setHasError(true);
    setIsLoaded(false);
    if (onError) onError(e);
  };

  // If no src provided at all, show fallback placeholder immediately
  const isPlaceholderActive = !isLoaded || hasError || !src;

  return (
    <div
      className={`acm-image-loader-wrapper ${wrapperClassName} ${isLoaded ? "is-loaded" : "is-loading"} ${hasError ? "has-error" : ""}`}
      style={wrapperStyle}
      onClick={onClick}
      title={title}
    >
      {/* Loading Placeholder with Glowing ACM Logo */}
      {isPlaceholderActive && (
        <div className={`acm-image-placeholder ${isLoaded ? "placeholder-fade-out" : ""}`}>
          <div className="acm-placeholder-shimmer"></div>
          <div className="acm-placeholder-content">
            <div className="acm-logo-pulse-box">
              <img
                src={logoSrc}
                alt="Loading..."
                className="acm-placeholder-logo"
              />
              <div className="acm-pulse-ring"></div>
            </div>
            {hasError && (
              <span className="acm-placeholder-fallback-text">
                Image Not Available
              </span>
            )}
          </div>
        </div>
      )}

      {/* Target Image */}
      {src && !hasError && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`acm-loaded-image ${className} ${isLoaded ? "image-visible" : "image-hidden"}`}
          style={style}
          loading={loading}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      )}
    </div>
  );
};

export default ImageLoader;
